import LocationPicker from '@/features/locations/components/LocationPicker';
import TurnstileWidget from './TurnstileWidget';

const fieldDescription = (name, fieldErrors, hint) => {
  const ids = [];
  if (fieldErrors[name]) ids.push(`${name}-error`);
  if (hint) ids.push(`${name}-hint`);
  return ids.length ? ids.join(' ') : undefined;
};

function SignupField({ name, label, required, optional, hint, fullWidth, fieldErrors, children }) {
  const error = fieldErrors[name];
  return (
    <div className={`form-group signup-field${fullWidth ? ' signup-field-full' : ''}`}>
      <label htmlFor={name}>
        {label}
        {required && <span className="required-star" aria-hidden="true"> *</span>}
        {optional && <span className="optional-tag"> (اختیاری)</span>}
      </label>
      {children(fieldDescription(name, fieldErrors, hint), Boolean(error))}
      {hint && <small id={`${name}-hint`} className="signup-field-hint">{hint}</small>}
      {error && <p id={`${name}-error`} className="signup-field-error" role="alert">{error}</p>}
    </div>
  );
}

function TextInput({ name, fieldErrors, hint, ...props }) {
  return (
    <SignupField
      name={name}
      label={props.label}
      required={props.required}
      optional={props.optional}
      fullWidth={props.fullWidth}
      hint={hint}
      fieldErrors={fieldErrors}
    >
      {(describedBy, invalid) => (
        <input
          id={name}
          name={name}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          {...props.inputProps}
        />
      )}
    </SignupField>
  );
}

export default function SignupFormFields({
  signupType,
  selectedType,
  formData,
  fieldErrors,
  errorSummary,
  errorSummaryRef,
  showPassword,
  showConfirmPassword,
  loading,
  turnstileSiteKey,
  turnstileRef,
  onSubmit,
  onSelectType,
  onChange,
  onDigitChange,
  onPhoneChange,
  onLocationChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onTurnstileToken,
  turnstileToken,
}) {
  const isVendor = signupType === 'vendor' || signupType === 'factory';
  const isCompany = signupType === 'company';
  const personalTitle = isCompany ? 'اطلاعات نماینده شرکت' : 'اطلاعات فردی';
  const latinDigits = { dir: 'ltr', inputMode: 'numeric', className: 'signup-input-ltr' };

  const renderText = (name, label, inputProps, options = {}) => (
    <TextInput
      key={name}
      name={name}
      label={label}
      required={options.required}
      optional={options.optional}
      fullWidth={options.fullWidth}
      hint={options.hint}
      fieldErrors={fieldErrors}
      inputProps={inputProps}
    />
  );

  const renderPassword = (name, label, visible, toggle, optional = false) => (
    <SignupField
      key={name}
      name={name}
      label={label}
      required={!optional}
      optional={optional}
      fieldErrors={fieldErrors}
    >
      {(describedBy, invalid) => (
        <div className="password-field">
          <input
            id={name}
            name={name}
            type={visible ? 'text' : 'password'}
            value={formData[name]}
            onChange={onChange}
            minLength={8}
            autoComplete={name === 'password' ? 'new-password' : 'new-password'}
            aria-invalid={invalid}
            aria-describedby={describedBy}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={toggle}
            aria-label={visible ? `مخفی کردن ${label}` : `نمایش ${label}`}
          >
            <i className={`fas ${visible ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
          </button>
        </div>
      )}
    </SignupField>
  );

  return (
    <form className="signup-form" onSubmit={onSubmit} noValidate>
      {errorSummary && (
        <div
          ref={errorSummaryRef}
          className="signup-error-summary"
          role="alert"
          tabIndex={-1}
        >
          {errorSummary}
        </div>
      )}

      <div className="signup-selected-type">
        <span className="signup-selected-type-label">
          <span className="signup-selected-type-icon" aria-hidden="true">
            <i className={`fas ${selectedType?.icon ?? 'fa-user'}`} />
          </span>
          <span>{selectedType?.title}</span>
        </span>
        <button type="button" className="signup-edit-link" onClick={onSelectType}>تغییر</button>
      </div>

      <section className="signup-form-section" aria-labelledby="signup-personal-title">
        <h2 id="signup-personal-title" className="signup-section-title">{personalTitle}</h2>
        <div className="signup-form-grid two-col">
          {renderText('first_name', 'نام', {
            type: 'text',
            value: formData.first_name,
            onChange,
            maxLength: 50,
            autoComplete: 'given-name',
            required: true,
          }, { required: true })}
          {renderText('last_name', 'نام خانوادگی', {
            type: 'text',
            value: formData.last_name,
            onChange,
            maxLength: 50,
            autoComplete: 'family-name',
            required: true,
          }, { required: true })}
          {renderText('mobile_phone', 'شماره موبایل', {
            type: 'tel',
            value: formData.mobile_phone,
            onChange: onPhoneChange,
            placeholder: '9123456789',
            maxLength: 10,
            autoComplete: 'tel-national',
            required: true,
            ...latinDigits,
          }, { required: true })}
          {renderText('national_id', 'کد ملی', {
            type: 'text',
            value: formData.national_id,
            onChange: (event) => onDigitChange(event, 10),
            placeholder: '1234567890',
            maxLength: 10,
            required: true,
            ...latinDigits,
          }, { required: true })}
        </div>
      </section>

      <section className="signup-form-section" aria-labelledby="signup-location-title">
        <h2 id="signup-location-title" className="signup-section-title">موقعیت</h2>
        <LocationPicker
          value={{
            location_id: formData.location_id,
            latitude: formData.latitude,
            longitude: formData.longitude,
            address: formData.address,
          }}
          onChange={onLocationChange}
        />
      </section>

      <section className="signup-form-section" aria-labelledby="signup-login-title">
        <h2 id="signup-login-title" className="signup-section-title">اطلاعات ورود</h2>
        <div className="signup-form-grid two-col">
          {renderText('email', 'ایمیل', {
            type: 'email',
            value: formData.email,
            onChange,
            placeholder: 'name@example.com',
            autoComplete: 'email',
            ...{ dir: 'ltr', className: 'signup-input-ltr' },
          }, { optional: true, fullWidth: true })}
          {renderPassword('password', 'رمز عبور', showPassword, onTogglePassword)}
          {renderPassword('confirmPassword', 'تکرار رمز عبور', showConfirmPassword, onToggleConfirmPassword)}
        </div>
      </section>

      {isVendor && (
        <section className="signup-form-section" aria-labelledby="signup-shop-title">
          <h2 id="signup-shop-title" className="signup-section-title">اطلاعات فروشگاه</h2>
          <div className="signup-form-grid two-col">
            {renderText('vendor_shop_name_fa', 'نام فروشگاه (فارسی)', {
              type: 'text',
              value: formData.vendor_shop_name_fa,
              onChange,
              maxLength: 64,
              required: true,
            }, { required: true })}
            {renderText('vendor_shop_name_en', 'نام فروشگاه (لاتین)', {
              type: 'text',
              value: formData.vendor_shop_name_en,
              onChange,
              maxLength: 64,
              dir: 'ltr',
              className: 'signup-input-ltr',
              required: true,
            }, { required: true, hint: 'برای ساخت آدرس اینترنتی فروشگاه استفاده می‌شود.' })}
            {renderText('vendor_local_phone', 'تلفن ثابت فروشگاه', {
              type: 'tel',
              value: formData.vendor_local_phone,
              onChange: (event) => onDigitChange(event, 10),
              placeholder: '3512341234',
              maxLength: 10,
              required: true,
              ...latinDigits,
            }, { required: true, hint: 'با کد شهر، بدون فاصله؛ دقیقاً ۱۰ رقم.' })}
            {renderText('shaba_number', 'شماره شبا', {
              type: 'text',
              value: formData.shaba_number,
              onChange: (event) => onChange({
                target: { name: 'shaba_number', value: event.target.value.toUpperCase() },
              }),
              placeholder: 'IR123456789012345678901234',
              maxLength: 26,
              dir: 'ltr',
              className: 'signup-input-ltr shaba-input',
              required: true,
            }, { required: true, hint: 'IR به‌همراه ۲۴ رقم؛ بدون فاصله.' })}
          </div>
        </section>
      )}

      {isCompany && (
        <section className="signup-form-section" aria-labelledby="signup-company-title">
          <h2 id="signup-company-title" className="signup-section-title">اطلاعات شرکت</h2>
          <div className="signup-form-grid two-col">
            {renderText('company_name_fa', 'نام شرکت', {
              type: 'text',
              value: formData.company_name_fa,
              onChange,
              maxLength: 256,
              required: true,
            }, { required: true })}
            {renderText('company_name_en', 'نام انگلیسی شرکت', {
              type: 'text',
              value: formData.company_name_en,
              onChange,
              maxLength: 256,
              dir: 'ltr',
              className: 'signup-input-ltr',
            }, { optional: true })}
            {renderText('registration_number', 'شناسه ملی شرکت', {
              type: 'text',
              value: formData.registration_number,
              onChange: (event) => onDigitChange(event, 11),
              placeholder: '12345678901',
              maxLength: 11,
              required: true,
              ...latinDigits,
            }, { required: true, hint: 'دقیقاً ۱۱ رقم.' })}
            {renderText('economic_code', 'کد اقتصادی', {
              type: 'text',
              value: formData.economic_code,
              onChange: (event) => onDigitChange(event, 64),
              placeholder: '123456789',
              dir: 'ltr',
              inputMode: 'numeric',
              className: 'signup-input-ltr',
            }, { optional: true })}
          </div>
        </section>
      )}

      {turnstileSiteKey && (
        <div className="signup-turnstile">
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            action="register_otp"
            onToken={onTurnstileToken}
            onExpire={() => onTurnstileToken('')}
            onError={() => onTurnstileToken('')}
          />
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary signup-submit-button"
        disabled={loading || (!!turnstileSiteKey && !turnstileToken)}
      >
        {loading ? <><span className="signup-spinner" aria-hidden="true" /> در حال ارسال...</> : 'ادامه و دریافت کد تأیید'}
      </button>
    </form>
  );
}
